import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LegalConsentComponent } from '../../../legal/legal-consent/legal-consent.component';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CreateCustomsCalculationRequest, CreateCustomsProductRequest } from '../../../../services/api/customs-calculations';
import { LanguageCode, LanguageService } from '../../../../services/i18n/language';
import { CurrencyRatesService } from '../../../../services/currency-rates';
import { Subscription } from 'rxjs';

export const CUSTOMS_CURRENCIES = ['USD', 'EUR', 'CNY'] as const;

const NUMBER_LOCALES: Record<LanguageCode, string> = { ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' };

@Component({
  selector: 'app-create-calculation-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LegalConsentComponent],
  templateUrl: './create-calculation-form.html',
  styleUrl: './create-calculation-form.scss'
})
export class CreateCalculationFormComponent implements OnInit, OnDestroy {
  @Input() backRoute: string = '/';
  @Input() submitting = false;
  @Output() cancel = new EventEmitter<void>();
  @Output() calculationCreated = new EventEmitter<{ calculation: CreateCustomsCalculationRequest; products: CreateCustomsProductRequest[] }>();
  @Output() invoiceUploaded = new EventEmitter<{ file: File; title?: string }>();

  calculationForm: FormGroup;
  error: string | null = null;
  activeTab: 'manual' | 'invoice' = 'manual';
  invoiceFile: File | null = null;
  dragActive = false;
  titleOpen = false;

  // Translations
  titleText = 'Предварительный расчёт';
  manualTabText = 'Вручную';
  invoiceTabText = 'По инвойсу';
  calculationTitleText = 'Название расчёта';
  productsText = 'Товары';
  addProductText = 'Добавить ещё товар';
  productText = 'Товар';
  productNameText = 'Название товара';
  productCostText = 'Общая стоимость товара';
  applicationScopeHintText = 'Например: каркас из металла, сиденье из ткани, регулируется по высоте, для офиса';
  applicationScopeNoteText = 'Напишите, из чего сделан товар, для чего он нужен, его марку и модель (если есть) — так код ТН ВЭД подберём точнее.';
  currencyText = 'Валюта';
  inKztText = 'В тенге';
  applicationScopeText = 'Описание товара';
  submitText = 'Получить расчёт';
  submittingText = 'Считаем...';
  invoiceCalculatingText = 'Идёт расчёт по инвойсу. Это может занять несколько минут...';
  cancelText = 'Отмена';
  requiredText = '*';
  removeText = 'Удалить';
  maxProductsHintText = 'Вручную можно добавить до 3 товаров. Если товаров больше — загрузите инвойс.';
  detailedAssessmentHintText = 'Это предварительная оценка. Итоговую сумму таможенных платежей утверждает таможенный орган при оформлении декларации.';
  readonly whatsappUrl = 'https://wa.me/77078880054';

  // Боковая панель (на десктопе справа от формы, на телефоне под ней)
  asideTitleText = 'Что вы получите';
  asideItems = [
    'Коды ТН ВЭД для каждого товара',
    'Пошлина, НДС, акциз и сборы в тенге',
    'Прогноз стоимости перевозки',
    'Проверка запретов и разрешительных документов',
  ];
  asideStepsTitleText = 'Как это работает';
  asideSteps = [
    'Заполните данные о товарах или загрузите инвойс',
    'Платформа подберёт коды ТН ВЭД и посчитает предварительные платежи',
    'Затем можно узнать прогноз стоимости доставки и нужные разрешительные документы',
  ];
  asideHelpTitleText = 'Нужна помощь?';
  asideHelpText = 'Ответим в WhatsApp и поможем с расчётом.';
  asideHelpBtnText = 'Написать в WhatsApp';
  asideHelpMessage = 'Здравствуйте! Нужна помощь с расчётом таможенных платежей.';

  get asideWhatsappUrl(): string {
    return `${this.whatsappUrl}?text=${encodeURIComponent(this.asideHelpMessage)}`;
  }
  invoiceUploadTitleText = 'Загрузка инвойса (PDF)';
  invoiceChooseFileText = 'Выбрать файл';
  invoiceSelectedFileText = 'Выбран файл';
  maxLength300Text = 'Не больше 300 символов';
  requiredFieldText = 'Обязательное поле';
  addAtLeastOneProductText = 'Добавьте хотя бы один товар';
  addTitleText = 'Назвать расчёт, чтобы найти потом';
  introEyebrowText = 'Бесплатно · Без регистрации';

  /** Что входит в расчёт — строка меток под заголовком. */
  features = [
    { title: 'Код ТН ВЭД' },
    { title: 'Таможенные платежи' },
    { title: 'Прогноз перевозки' },
    { title: 'Разрешительные документы' },
  ];
  manualHintText = 'Впишите до 3 товаров';
  invoiceHintText = 'Загрузите PDF — товары распознаем сами';
  optionalText = 'необязательно';
  productNamePlaceholderText = 'Например: офисное кресло';
  goodsTotalLabelText = 'Итого за {n} товара';
  dropzoneTitleText = 'Загрузите инвойс в PDF';
  dropzoneHintText = 'Перетащите файл сюда или выберите его на устройстве';
  fileReplaceText = 'Заменить';
  fileRemoveText = 'Убрать';
  uploadStep1Text = 'Распознаем товары и суммы';
  uploadStep2Text = 'Определим коды ТН ВЭД';
  uploadStep3Text = 'Посчитаем таможенные платежи';
  nameRequiredText = 'Укажите название товара';
  costRequiredText = 'Укажите стоимость больше нуля';
  pdfOnlyText = 'Подходит только PDF-файл';
  fixFieldsText = 'Заполните поля, отмеченные ниже';
  selectInvoiceFirstText = 'Сначала выберите PDF-файл инвойса';
  countLabelText = '{n} из {max}';
  titlePlaceholderText = 'Например: кресла из Гуанчжоу';

  private subscriptions = new Subscription();

  rates: Record<string, number> = { USD: 501, EUR: 597, CNY: 72 };
  numberLocale = 'ru-RU';

  constructor(
    private fb: FormBuilder,
    private languageService: LanguageService,
    private currencyRates: CurrencyRatesService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.calculationForm = this.fb.group({
      title: [''],
      products: this.fb.array([])
    });
  }

  get productCurrencies() {
    return CUSTOMS_CURRENCIES;
  }

  getProductCostKzt(product: FormGroup | AbstractControl): number | null {
    const cost = product.get('productCost')?.value;
    const code = product.get('currencyCode')?.value || 'USD';
    if (cost == null || cost === '') return null;
    const rate = code === 'KZT' ? 1 : (this.rates[code] ?? 0);
    return Number(cost) * rate;
  }

  /** Сумма стоимости введённых товаров в тенге (по текущим курсам). */
  get goodsTotalKzt(): number {
    return this.products.controls.reduce((sum, c) => sum + (this.getProductCostKzt(c) ?? 0), 0);
  }

  /** Сколько товаров уже с суммой — итог показываем от двух, при одном он повторял бы сумму под полем. */
  get pricedProductsCount(): number {
    return this.products.controls.filter((c) => (this.getProductCostKzt(c) ?? 0) > 0).length;
  }

  get goodsTotalLabel(): string {
    return this.goodsTotalLabelText.replace('{n}', String(this.pricedProductsCount));
  }

  /** Тенге с разделителем тысяч по языку: «2 256 550» для ru/kk, «2,256,550» для en. */
  formatKzt(value: number | null): string {
    return new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 0 }).format(value ?? 0);
  }

  isInvalid(group: AbstractControl, name: string): boolean {
    const control = group.get(name);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  ngOnInit(): void {
    this.updateTranslations();
    this.addProduct(); // по умолчанию ручной режим

    this.subscriptions.add(
      this.currencyRates.getRates().subscribe((r) => {
        this.rates = { ...r };
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.add(
      this.languageService.getCurrentLanguage().subscribe((lang) => {
        this.numberLocale = NUMBER_LOCALES[lang] ?? 'ru-RU';
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get products(): FormArray {
    return this.calculationForm.get('products') as FormArray;
  }

  readonly maxCustomsProducts = 3;

  addProduct(): void {
    if (this.products.length >= this.maxCustomsProducts) return;
    const productGroup = this.fb.group({
      nameOriginal: ['', [Validators.required]],
      productCost: [null, [Validators.required, Validators.min(0.01)]],
      currencyCode: ['USD'],
      applicationScope: ['', [Validators.maxLength(300)]]
    });
    this.products.push(productGroup);
  }

  /** Добавляет товар по кнопке и ставит курсор в его название — на телефоне сразу открывается клавиатура. */
  addProductAndFocus(): void {
    this.addProduct();
    this.focusLater('#good-name-' + (this.products.length - 1));
  }

  openTitle(): void {
    this.titleOpen = true;
    this.focusLater('#calc-title');
  }

  private focusLater(selector: string): void {
    this.cdr.markForCheck();
    setTimeout(() => (document.querySelector(selector) as HTMLElement | null)?.focus(), 0);
  }

  removeProduct(index: number): void {
    this.products.removeAt(index);
  }

  onSubmit(): void {
    if (this.activeTab === 'invoice') {
      if (!this.invoiceFile) {
        this.error = this.selectInvoiceFirstText;
        this.cdr.markForCheck();
        return;
      }
      const title = (this.calculationForm.get('title')?.value || '').toString();
      this.invoiceUploaded.emit({ file: this.invoiceFile, title: title?.trim() || undefined });
      return;
    }

    if (this.calculationForm.invalid || this.products.length === 0) {
      this.calculationForm.markAllAsTouched();
      this.error = this.products.length === 0 ? this.addAtLeastOneProductText : this.fixFieldsText;
      this.cdr.markForCheck();
      setTimeout(() => (document.querySelector('.calc-new .is-invalid') as HTMLElement | null)?.focus(), 0);
      return;
    }

    // Состояние «Считаем...» задаёт страница через @Input: если она сначала спросит телефон
    // и человек закроет окно, кнопка не должна зависнуть заблокированной
    this.error = null;

    const formValue = this.calculationForm.value;
    const request: CreateCustomsCalculationRequest = {
      title: formValue.title || undefined
    };

    const productRequests: CreateCustomsProductRequest[] = formValue.products.map((p: any) => ({
      nameOriginal: p.nameOriginal,
      productCost: p.productCost,
      currencyCode: p.currencyCode || 'USD',
      applicationScope: p.applicationScope
    }));

    // Эмитим событие с данными для создания
    this.calculationCreated.emit({
      calculation: request,
      products: productRequests
    } as any);
  }

  onCancel(): void {
    this.cancel.emit();
  }

  switchTab(tab: 'manual' | 'invoice'): void {
    this.activeTab = tab;
    this.error = null;
    this.cdr.markForCheck();
  }

  onInvoiceSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files && input.files.length > 0 ? input.files[0] : null;
    this.setInvoiceFile(file);
    if (input) input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = false;
    const file = event.dataTransfer?.files?.[0] ?? null;
    this.setInvoiceFile(file);
  }

  removeInvoice(event?: Event): void {
    event?.stopPropagation();
    this.invoiceFile = null;
    this.error = null;
    this.cdr.markForCheck();
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024 * 1024) return Math.max(1, Math.round(bytes / 1024)) + ' КБ';
    return (bytes / (1024 * 1024)).toFixed(1).replace('.', ',') + ' МБ';
  }

  private setInvoiceFile(file: File | null): void {
    if (file && !(file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'))) {
      this.error = this.pdfOnlyText;
      this.cdr.markForCheck();
      return;
    }
    this.invoiceFile = file;
    this.error = null;
    this.cdr.markForCheck();
  }

  /** Счётчик товаров: «1 из 3». */
  get countLabel(): string {
    return this.countLabelText
      .replace('{n}', String(this.products.length))
      .replace('{max}', String(this.maxCustomsProducts));
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('public.createCalculation.title') || this.titleText;
    this.manualTabText = this.languageService.translate('public.createCalculation.manualTab') || this.manualTabText;
    this.invoiceTabText = this.languageService.translate('public.createCalculation.invoiceTab') || this.invoiceTabText;
    this.calculationTitleText = this.languageService.translate('public.createCalculation.calculationTitle') || this.calculationTitleText;
    this.productsText = this.languageService.translate('public.createCalculation.products') || this.productsText;
    this.addProductText = this.languageService.translate('public.createCalculation.addProduct') || this.addProductText;
    this.productText = this.languageService.translate('public.createCalculation.productText') || this.productText;
    this.productNameText = this.languageService.translate('public.createCalculation.productName') || this.productNameText;
    this.applicationScopeHintText = this.languageService.translate('public.createCalculation.applicationScopeHint') || this.applicationScopeHintText;
    this.productCostText = this.languageService.translate('public.createCalculation.productCost') || this.productCostText;
    this.currencyText = this.languageService.translate('public.createCalculation.currency') || this.currencyText;
    this.applicationScopeText = this.languageService.translate('public.createCalculation.applicationScope') || this.applicationScopeText;
    this.submitText = this.languageService.translate('public.createCalculation.submit') || this.submitText;
    this.submittingText = this.languageService.translate('public.createCalculation.submitting') || this.submittingText;
    this.cancelText = this.languageService.translate('common.cancel') || this.cancelText;
    this.removeText = this.languageService.translate('common.remove') || this.removeText;
    this.requiredText = this.languageService.translate('common.required') || this.requiredText;
    this.maxProductsHintText = this.languageService.translate('public.createCalculation.maxProductsHint') || this.maxProductsHintText;
    this.detailedAssessmentHintText = this.languageService.translate('public.createCalculation.detailedAssessmentHint') || this.detailedAssessmentHintText;
    this.invoiceUploadTitleText = this.languageService.translate('public.createCalculation.invoiceUploadTitle') || this.invoiceUploadTitleText;
    this.invoiceChooseFileText = this.languageService.translate('public.createCalculation.invoiceChooseFile') || this.invoiceChooseFileText;
    this.invoiceSelectedFileText = this.languageService.translate('public.createCalculation.invoiceSelectedFile') || this.invoiceSelectedFileText;
    this.invoiceCalculatingText = this.languageService.translate('public.createCalculation.invoiceCalculating') || this.invoiceCalculatingText;
    this.maxLength300Text = this.languageService.translate('public.createCalculation.maxLength300') || this.maxLength300Text;
    this.requiredFieldText = this.languageService.translate('cargoOwner.createRequest.requiredField') || this.requiredFieldText;
    this.addAtLeastOneProductText = this.languageService.translate('public.createCalculation.addAtLeastOneProduct') || this.addAtLeastOneProductText;
    this.addTitleText = this.t('public.createCalculation.addTitle', this.addTitleText);
    this.introEyebrowText = this.t('public.createCalculation.introEyebrow', this.introEyebrowText);
    this.features = this.features.map((f, i) => ({
      title: this.t(`public.createCalculation.feature${i + 1}Title`, f.title),
    }));
    this.countLabelText = this.t('public.createCalculation.countLabel', this.countLabelText);
    this.titlePlaceholderText = this.t('public.createCalculation.titlePlaceholder', this.titlePlaceholderText);
    this.manualHintText = this.t('public.createCalculation.manualHint', this.manualHintText);
    this.invoiceHintText = this.t('public.createCalculation.invoiceHint', this.invoiceHintText);
    this.optionalText = this.t('public.createCalculation.optional', this.optionalText);
    this.productNamePlaceholderText = this.t('public.createCalculation.productNamePlaceholder', this.productNamePlaceholderText);
    this.applicationScopeNoteText = this.t('public.createCalculation.applicationScopeNote', this.applicationScopeNoteText);
    this.inKztText = this.t('public.createCalculation.costInKzt', this.inKztText);
    this.goodsTotalLabelText = this.t('public.createCalculation.goodsTotalLabel', this.goodsTotalLabelText);
    this.dropzoneTitleText = this.t('public.createCalculation.dropzoneTitle', this.dropzoneTitleText);
    this.dropzoneHintText = this.t('public.createCalculation.dropzoneHint', this.dropzoneHintText);
    this.fileReplaceText = this.t('public.createCalculation.fileReplace', this.fileReplaceText);
    this.fileRemoveText = this.t('public.createCalculation.fileRemove', this.fileRemoveText);
    this.uploadStep1Text = this.t('public.createCalculation.uploadStep1', this.uploadStep1Text);
    this.uploadStep2Text = this.t('public.createCalculation.uploadStep2', this.uploadStep2Text);
    this.uploadStep3Text = this.t('public.createCalculation.uploadStep3', this.uploadStep3Text);
    this.nameRequiredText = this.t('public.createCalculation.nameRequired', this.nameRequiredText);
    this.costRequiredText = this.t('public.createCalculation.costRequired', this.costRequiredText);
    this.pdfOnlyText = this.t('public.createCalculation.pdfOnly', this.pdfOnlyText);
    this.fixFieldsText = this.t('public.createCalculation.fixFields', this.fixFieldsText);
    this.selectInvoiceFirstText = this.t('public.createCalculation.selectInvoiceFirst', this.selectInvoiceFirstText);

    this.asideTitleText = this.t('public.createCalculation.asideTitle', this.asideTitleText);
    this.asideItems = this.asideItems.map((text, i) => this.t(`public.createCalculation.asideItem${i + 1}`, text));
    this.asideStepsTitleText = this.t('public.createCalculation.asideStepsTitle', this.asideStepsTitleText);
    this.asideSteps = this.asideSteps.map((text, i) => this.t(`public.createCalculation.asideStep${i + 1}`, text));
    this.asideHelpTitleText = this.t('public.createCalculation.asideHelpTitle', this.asideHelpTitleText);
    this.asideHelpText = this.t('public.createCalculation.asideHelpText', this.asideHelpText);
    this.asideHelpBtnText = this.t('public.createCalculation.asideHelpBtn', this.asideHelpBtnText);
    this.asideHelpMessage = this.t('public.createCalculation.asideHelpMessage', this.asideHelpMessage);
  }
}
